import type { ArcadeScreen } from "../ScreenManager.js";
import type { MenuAction } from "../MenuInput.js";
import { NavList, type NavItem } from "../navList.js";
import { uiSfx } from "../uiSfx.js";

/**
 * PRACTICE — dummy match, test lab and comparison lab as proper entries
 * instead of classic footer buttons (docs/menu-redesign-plan.md §3.7).
 *
 * No functional change: each entry fires the exact code path of today's
 * footer buttons (main.ts wires the deps to `select.confirmDummy()` and the
 * shared test-lab opener). The classic UI has no bot preset/difficulty
 * pickers (those are URL params: ?dummy=…&difficulty=…), so none exist here
 * either — the sub-lines document what each mode does instead.
 */

export interface PracticeDeps {
  /** Same as the classic DUMMY MATCH footer button. */
  onDummy: () => void;
  /** Same as the classic TEST LAB footer button (lab picker, demo side). */
  onTestLab: () => void;
  /** Same picker, comparison side pre-focused. */
  onComparison: () => void;
  onBack: () => void;
}

interface PracticeEntry {
  id: "dummy" | "testlab" | "comparison";
  label: string;
  sub: string;
}

const ENTRIES: readonly PracticeEntry[] = [
  { id: "dummy", label: "DUMMY MATCH", sub: "SOLO ARENA VS A TRAINING BOT — LIVE FIRE, NO STAKES" },
  { id: "testlab", label: "TEST LAB", sub: "AUTOMATED DEMO RUNS — SPEED · CORNERING · DRIFT · COMBAT" },
  { id: "comparison", label: "COMPARISON LAB", sub: "TWO VEHICLES, MIRRORED INPUT — COMPARE HANDLING LIVE" },
];

const NOTICE_MS = 6000;

export class PracticeScreen implements ArcadeScreen {
  private noticeEl: HTMLElement | null = null;
  private notice: string | null = null;
  private noticeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly nav = new NavList(() => uiSfx.play("nav"));

  constructor(private readonly deps: PracticeDeps) {}

  mount(root: HTMLElement): void {
    root.innerHTML = `
      <div class="pr-wrap">
        <div class="pr-head">
          <span class="pr-eyebrow">PRACTICE</span>
          <span class="pr-headrule" aria-hidden="true"></span>
        </div>
        <nav class="pr-list" aria-label="Practice modes"></nav>
        <div class="pr-notice hidden"></div>
        <div class="pr-footer">
          <span class="pr-hints">↑↓ SELECT &nbsp;·&nbsp; ENTER / Ⓐ CONFIRM &nbsp;·&nbsp; ESC / Ⓑ BACK</span>
        </div>
      </div>`;

    const list = root.querySelector(".pr-list")!;
    const items: NavItem[] = [];
    ENTRIES.forEach((entry) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "pr-entry";
      btn.dataset["id"] = entry.id;
      btn.innerHTML = `<span class="pr-label">${entry.label}</span><span class="pr-sub">${entry.sub}</span>`;
      const idx = items.length;
      btn.addEventListener("mouseenter", () => this.nav.focusTo(idx, true));
      btn.addEventListener("focus", () => this.nav.focusTo(idx, true));
      btn.addEventListener("click", () => this.launch(entry.id));
      list.appendChild(btn);
      items.push({ el: btn, activate: () => this.launch(entry.id) });
    });
    this.nav.setItems(items);
    this.noticeEl = root.querySelector(".pr-notice");
  }

  onEnter(): void {
    this.nav.applyFocus();
    this.renderNotice();
  }

  onLeave(): void {
    /* keep focus index for the return trip */
  }

  handleInput(action: MenuAction): boolean {
    switch (action.type) {
      case "up":
        this.nav.move(-1);
        return true;
      case "down":
        this.nav.move(1);
        return true;
      case "confirm":
      case "start":
        this.nav.activate();
        return true;
      case "back":
        uiSfx.play("back");
        this.deps.onBack();
        return true;
      default:
        return false;
    }
  }

  /** Transient failure strip (e.g. dummy-match connect error). */
  setNotice(msg: string): void {
    this.notice = msg;
    if (this.noticeTimer !== null) clearTimeout(this.noticeTimer);
    this.noticeTimer = setTimeout(() => {
      this.notice = null;
      this.noticeTimer = null;
      this.renderNotice();
    }, NOTICE_MS);
    this.renderNotice();
  }

  // ── internals ───────────────────────────────────────────────────────────

  private launch(id: PracticeEntry["id"]): void {
    uiSfx.play("confirm");
    if (id === "dummy") this.deps.onDummy();
    else if (id === "testlab") this.deps.onTestLab();
    else this.deps.onComparison();
  }

  private renderNotice(): void {
    if (!this.noticeEl) return;
    if (this.notice) {
      this.noticeEl.textContent = this.notice;
      this.noticeEl.classList.remove("hidden");
    } else {
      this.noticeEl.textContent = "";
      this.noticeEl.classList.add("hidden");
    }
  }
}
